import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    Menu,
    MenuItem,
    ListItemIcon,
    Typography,
    Divider,
    Badge
} from '@mui/material';
import {
    Home as HomeIcon,
    Group as GroupIcon,
    Message as MessageIcon,
    Person as PersonIcon,
    Favorite as FavoriteIcon,
    Settings as SettingsIcon,
    Logout as LogoutIcon,
    ContactSupport as ContactSupportIcon,
    Help as HelpIcon,
    Security as SecurityIcon,
    Gavel as GavelIcon,
    Add as AddIcon,
    ListAlt as ListAltIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';

const MobileNav = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const { logout, currentUser } = useAuth();
    const [value, setValue] = useState(null);
    const [totalUnreadCount, setTotalUnreadCount] = useState(0);

    // 現在のパスに基づいてアクティブなタブを設定する
    useEffect(() => {
        const path = location.pathname;

        if (path === '/' || path.startsWith('/room-search')) {
            setValue('/room-search');
        } else if (path.startsWith('/renters')) {
            setValue('/renters');
        } else if (path.startsWith('/messages')) {
            setValue('/messages');
        } else if (path.startsWith('/profile') || path.startsWith('/settings') ||
            path.startsWith('/favorites') || path.startsWith('/my-rooms') ||
            path.startsWith('/create-listing')) {
            setValue('account');
        } else {
            setValue(null);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (!currentUser) return;

        const chatRoomsRef = collection(db, 'chatRooms');
        const q = query(
            chatRoomsRef,
            where('participants', 'array-contains', currentUser.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const unreadCount = snapshot.docs.reduce((total, doc) => {
                const data = doc.data();
                return total + (data.unreadCounts?.[currentUser.uid] || 0);
            }, 0);
            setTotalUnreadCount(unreadCount);
        });

        return () => unsubscribe();
    }, [currentUser]);

    // ホーム画面からのリセットイベントをリッスン
    useEffect(() => {
        const handleResetNavigation = () => {
            // ナビゲーションの選択状態をリセット
            setValue(null);
        };

        // カスタムイベントのリスナーを追加
        window.addEventListener('resetNavigation', handleResetNavigation);

        // クリーンアップ関数
        return () => {
            window.removeEventListener('resetNavigation', handleResetNavigation);
        };
    }, []);

    // ChatRoomページでは表示しない
    if (location.pathname.includes('/messages/')) {
        return null;
    }

    const navigation = [
        { label: '物件', path: '/room-search', icon: <HomeIcon /> },
        { label: 'ルームメイト', path: '/renters', icon: <GroupIcon /> },
        {
            label: 'メッセージ',
            path: '/messages',
            icon: totalUnreadCount > 0 ? (
                <Badge badgeContent={totalUnreadCount} color="primary">
                    <MessageIcon />
                </Badge>
            ) : <MessageIcon />
        },
        { label: 'アカウント', path: null, icon: <PersonIcon /> },
    ];

    const userMenuItems = [
        { text: 'プロフィール', icon: <PersonIcon />, path: '/profile' },
        { text: 'お気に入り', icon: <FavoriteIcon />, path: '/favorites' },
        { text: '物件を掲載', icon: <AddIcon />, path: '/create-listing' },
        { text: '登録した物件', icon: <ListAltIcon />, path: '/my-rooms' },
        { text: 'メッセージ', icon: <MessageIcon />, path: '/messages' },
        { text: '設定', icon: <SettingsIcon />, path: '/settings' },
    ];

    const helpMenuItems = [
        { text: 'お問い合わせ', icon: <ContactSupportIcon />, path: '/contact' },
        { text: 'ヘルプセンター', icon: <HelpIcon />, path: '/help' },
        { text: 'ブログ', icon: <ListAltIcon />, path: '/blog' },
        { text: 'プライバシーポリシー', icon: <SecurityIcon />, path: '/privacy' },
        { text: '利用規約', icon: <GavelIcon />, path: '/terms' },
    ];

    const handleAccountClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        try {
            await logout();
            handleMenuClose();
            navigate('/');
        } catch (error) {
            console.error('ログアウトエラー:', error);
        }
    };

    const handleNavigation = (path) => {
        if (path === null) {
            return;
        }
        navigate(path);
    };

    return (
        <>
            <Paper
                sx={{
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: '#fff',
                    display: { xs: 'block', md: 'none' },
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
                elevation={1}
            >
                <BottomNavigation
                    value={value}
                    onChange={(event, newValue) => {
                        setValue(newValue);
                        if (newValue && newValue !== 'account') {
                            navigate(newValue);
                        }
                    }}
                    sx={{
                        height: { xs: 60, sm: 70 },
                        '& .MuiBottomNavigationAction-root': {
                            minWidth: { xs: 'auto', sm: 100 },
                            padding: { xs: '8px 0', sm: '12px 16px' },
                            color: '#7f8c8d',
                            transition: 'all 0.2s ease',
                            '&.Mui-selected': {
                                color: '#3498db',
                                borderBottom: '3px solid #3498db',
                                borderTop: 'none',
                                '& .MuiSvgIcon-root': {
                                    color: '#3498db',
                                    transform: 'scale(1.1)'
                                },
                                '& .MuiBottomNavigationAction-label': {
                                    color: '#3498db',
                                    fontWeight: 700,
                                    transform: 'translateY(1px)'
                                }
                            },
                            '& .MuiBottomNavigationAction-label': {
                                opacity: 1,
                                fontSize: { xs: '0.7rem', sm: '0.85rem' },
                                transition: 'all 0.2s ease',
                                fontWeight: 500,
                                marginTop: '4px'
                            },
                            '& .MuiSvgIcon-root': {
                                fontSize: { xs: '1.2rem', sm: '1.4rem' }
                            },
                            '&:hover': {
                                backgroundColor: 'rgba(52, 152, 219, 0.04)',
                                transform: { sm: 'translateY(-1px)' }
                            }
                        }
                    }}
                >
                    {navigation.map((item) => (
                        <BottomNavigationAction
                            key={item.label}
                            label={item.label}
                            value={item.path || 'account'}
                            icon={item.icon}
                            onClick={item.path ? () => handleNavigation(item.path) : handleAccountClick}
                        />
                    ))}
                </BottomNavigation>
            </Paper>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                sx={{
                    mt: -1,
                    '& .MuiPaper-root': {
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        minWidth: { xs: 200, sm: 240 }
                    }
                }}
            >
                {!currentUser && (
                    <MenuItem
                        onClick={() => {
                            handleMenuClose();
                            navigate('/login');
                        }}
                        sx={{
                            py: { xs: 1.5, sm: 2 },
                            px: { xs: 2, sm: 3 },
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                transform: 'translateX(4px)'
                            }
                        }}
                    >
                        <ListItemIcon sx={{
                            color: '#3498db',
                            minWidth: { xs: 36, sm: 40 }
                        }}>
                            <PersonIcon />
                        </ListItemIcon>
                        <Typography
                            sx={{
                                fontWeight: 500,
                                color: '#2c3e50',
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                            }}
                        >
                            ログイン
                        </Typography>
                    </MenuItem>
                )}

                {userMenuItems.map((item) => (
                    <MenuItem
                        key={item.text}
                        onClick={() => {
                            handleMenuClose();
                            navigate(item.path);
                        }}
                        sx={{
                            py: { xs: 1.5, sm: 2 },
                            px: { xs: 2, sm: 3 },
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                transform: 'translateX(4px)'
                            }
                        }}
                    >
                        <ListItemIcon sx={{
                            color: '#3498db',
                            minWidth: { xs: 36, sm: 40 }
                        }}>
                            {item.icon}
                        </ListItemIcon>
                        <Typography
                            sx={{
                                fontWeight: 500,
                                color: '#2c3e50',
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                            }}
                        >
                            {item.text}
                        </Typography>
                    </MenuItem>
                ))}
                <Divider sx={{ my: 1 }} />
                {helpMenuItems.map((item) => (
                    <MenuItem
                        key={item.text}
                        onClick={() => {
                            handleMenuClose();
                            navigate(item.path);
                        }}
                        sx={{
                            py: { xs: 1.5, sm: 2 },
                            px: { xs: 2, sm: 3 },
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                transform: 'translateX(4px)'
                            }
                        }}
                    >
                        <ListItemIcon sx={{
                            color: '#7f8c8d',
                            minWidth: { xs: 36, sm: 40 }
                        }}>
                            {item.icon}
                        </ListItemIcon>
                        <Typography
                            sx={{
                                fontWeight: 500,
                                color: '#2c3e50',
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                            }}
                        >
                            {item.text}
                        </Typography>
                    </MenuItem>
                ))}
                {currentUser && [
                    <Divider key="divider" sx={{ my: 1 }} />,
                    <MenuItem
                        key="logout"
                        onClick={handleLogout}
                        sx={{
                            py: { xs: 1.5, sm: 2 },
                            px: { xs: 2, sm: 3 },
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(231, 76, 60, 0.08)',
                                transform: 'translateX(4px)'
                            }
                        }}
                    >
                        <ListItemIcon sx={{
                            color: '#e74c3c',
                            minWidth: { xs: 36, sm: 40 }
                        }}>
                            <LogoutIcon />
                        </ListItemIcon>
                        <Typography
                            sx={{
                                fontWeight: 500,
                                color: '#e74c3c',
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                            }}
                        >
                            ログアウト
                        </Typography>
                    </MenuItem>
                ]}
            </Menu>
        </>
    );
}

export default MobileNav; 